import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, ChevronDown, ChevronRight, Download, Trash2, Filter, FileText,
  Paperclip, Pencil, Truck, Package, AlertTriangle, CheckCircle, Clock, Link2, X
} from 'lucide-react';
import VendorSelect from '@/components/VendorSelect';
import AttachmentPreview from '@/components/AttachmentPreview';

const STATES = ['Order Placed', 'In Process', 'Out for Delivery', 'Delivered'];
const STATE_ICON = { 'Order Placed': Package, 'In Process': Clock, 'Out for Delivery': Truck, 'Delivered': CheckCircle };
const STATE_COLOR = {
  'Order Placed': 'bg-[#E8E6E1] text-[#2C2C2E]',
  'In Process': 'bg-[#3A8C8C]/10 text-[#3A8C8C]',
  'Out for Delivery': 'bg-[#C67D5B]/10 text-[#C67D5B]',
  'Delivered': 'bg-[#7DA68A]/10 text-[#7DA68A]',
};

const HIGH_VALUE_THRESHOLD = 100000; // Rs 1L

const formatINR = (n) => (!n && n !== 0) ? '0' : Number(n).toLocaleString('en-IN');
const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};
const today = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.floor((new Date(a).getTime() - new Date(b).getTime()) / 86400000);

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Computed delivery state (rule-based)
function getDeliveryState(order) {
  if (order.status === 'Delivered') return { label: 'Delivered', color: 'bg-[#7DA68A]/10 text-[#7DA68A]' };
  const tent = order.tentative_delivery_date;
  if (!tent) return { label: 'On Track', color: 'bg-[#7DA68A]/10 text-[#7DA68A]' };
  const days = daysBetween(today(), tent);
  if (days > 0) return { label: 'Delayed', color: 'bg-[#C4727F]/10 text-[#C4727F]' };
  if (days >= -3) return { label: 'At Risk', color: 'bg-[#C67D5B]/10 text-[#C67D5B]' };
  return { label: 'On Track', color: 'bg-[#7DA68A]/10 text-[#7DA68A]' };
}

function deliveryDelayDays(order) {
  if (!order.tentative_delivery_date) return null;
  const ref = order.actual_delivery_date || (order.status === 'Delivered' ? null : today());
  if (!ref) return null;
  return daysBetween(ref, order.tentative_delivery_date);
}

const emptyForm = {
  order_number: '', vendor_name: '', item_description: '', order_amount: '',
  order_date: '', tentative_delivery_date: '', delivery_location: '', notes: '',
  actual_delivery_date: '', actual_delivery_time: '', received_by: '',
  bill_id: null, status: 'Order Placed'
};

export default function ProjectOrders({ projectId, projectName, ordersData, billingData, onUpdate, onBillingUpdate }) {
  const [entries, setEntries] = useState(ordersData || []);
  const [bills, setBills] = useState(billingData || []);
  const [expanded, setExpanded] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterState, setFilterState] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [sortBy, setSortBy] = useState('urgency');
  const [attachTarget, setAttachTarget] = useState(null);
  const [previewAtt, setPreviewAtt] = useState(null);
  const fileRef = useRef(null);

  // Bill creation from inside an order
  const [createBillFor, setCreateBillFor] = useState(null); // 'add' | 'edit'
  const [billForm, setBillForm] = useState({
    bill_type: 'Vendor Payment', vendor_name: '', bill_number: '', bill_date: '', bill_amount: '', notes: ''
  });

  useEffect(() => { setEntries(ordersData || []); }, [ordersData]);
  useEffect(() => { setBills(billingData || []); }, [billingData]);

  // Recommended vendor based on most recent order
  const recommendedVendor = useMemo(() => {
    if (!entries.length) return '';
    const sorted = [...entries].sort((a, b) => (b.order_date || '').localeCompare(a.order_date || ''));
    return sorted[0]?.vendor_name || '';
  }, [entries]);

  const enriched = useMemo(() => entries.map(o => {
    const linkedBill = o.bill_id ? bills.find(b => b.id === o.bill_id) : null;
    const totalPaid = linkedBill ? (linkedBill.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0) : 0;
    const billAmt = linkedBill ? Number(linkedBill.bill_amount || 0) : 0;
    const billPending = Math.max(0, billAmt - totalPaid);
    const computed = getDeliveryState(o);
    const delay = deliveryDelayDays(o);
    const orderAge = o.order_date ? daysBetween(today(), o.order_date) : 0;
    const amountMismatch = linkedBill && Number(o.order_amount || 0) > 0 && Math.abs(Number(o.order_amount) - billAmt) > 1;
    return { ...o, _linkedBill: linkedBill, _billPaid: totalPaid, _billPending: billPending, _state: computed, _delay: delay, _orderAge: orderAge, _amountMismatch: amountMismatch };
  }), [entries, bills]);

  const filtered = useMemo(() => {
    let r = enriched;
    if (filterState !== 'all') r = r.filter(o => o.status === filterState);
    if (quickFilter === 'delayed') r = r.filter(o => o._state.label === 'Delayed');
    if (quickFilter === 'due_today') r = r.filter(o => o.tentative_delivery_date === today() && o.status !== 'Delivered');
    if (quickFilter === 'delivered') r = r.filter(o => o.status === 'Delivered');
    if (quickFilter === 'high_value') r = r.filter(o => Number(o.order_amount || 0) >= HIGH_VALUE_THRESHOLD);
    if (sortBy === 'date') {
      r = [...r].sort((a, b) => (b.order_date || '').localeCompare(a.order_date || ''));
    } else {
      const rank = { Delayed: 3, 'At Risk': 2, 'On Track': 1, Delivered: 0 };
      r = [...r].sort((a, b) => (rank[b._state.label] || 0) - (rank[a._state.label] || 0));
    }
    return r;
  }, [enriched, filterState, quickFilter, sortBy]);

  const totals = useMemo(() => ({
    count: enriched.length,
    delayed: enriched.filter(o => o._state.label === 'Delayed').length,
    delivered: enriched.filter(o => o.status === 'Delivered').length,
    deliveredUnpaid: enriched.filter(o => o.status === 'Delivered' && o._billPending > 0).length,
    totalValue: enriched.reduce((s, o) => s + Number(o.order_amount || 0), 0),
  }), [enriched]);

  const submitForm = async (mode) => {
    if (!form.vendor_name.trim() || !form.item_description.trim()) {
      toast.error('Vendor and item description are required'); return;
    }
    const payload = { ...form, order_amount: Number(form.order_amount) || 0 };
    try {
      if (mode === 'add') {
        const res = await api.post(`/projects/${projectId}/orders`, payload);
        const updated = [...entries, res.data];
        setEntries(updated); onUpdate?.(updated);
        toast.success('Order added');
        setShowAdd(false);
      } else {
        await api.put(`/projects/${projectId}/orders/${showEdit}`, payload);
        const updated = entries.map(o => o.id === showEdit ? { ...o, ...payload } : o);
        setEntries(updated); onUpdate?.(updated);
        toast.success('Order updated');
        setShowEdit(null);
      }
      setForm(emptyForm);
    } catch { toast.error('Save failed'); }
  };

  const openEdit = (o) => {
    setShowEdit(o.id);
    setForm({
      order_number: o.order_number || '', vendor_name: o.vendor_name || '',
      item_description: o.item_description || '', order_amount: o.order_amount || '',
      order_date: o.order_date || '', tentative_delivery_date: o.tentative_delivery_date || '',
      delivery_location: o.delivery_location || '', notes: o.notes || '',
      actual_delivery_date: o.actual_delivery_date || '', actual_delivery_time: o.actual_delivery_time || '',
      received_by: o.received_by || '', bill_id: o.bill_id || null, status: o.status || 'Order Placed'
    });
  };

  const deleteEntry = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${projectId}/orders/${deleteTarget}`);
      const updated = entries.filter(o => o.id !== deleteTarget);
      setEntries(updated); onUpdate?.(updated);
      toast.success('Order deleted');
    } catch { toast.error('Delete failed'); }
    setDeleteTarget(null);
  };

  const transition = async (orderId, newState) => {
    try {
      const data = { state: newState };
      if (newState === 'Delivered' && !entries.find(e => e.id === orderId)?.actual_delivery_date) {
        data.actual_delivery_date = today();
      }
      await api.post(`/projects/${projectId}/orders/${orderId}/transition`, data);
      const updated = entries.map(o => o.id === orderId ? {
        ...o,
        status: newState,
        ...(data.actual_delivery_date ? { actual_delivery_date: data.actual_delivery_date } : {}),
        status_history: [...(o.status_history || []), { state: newState, at: new Date().toISOString(), by: '' }]
      } : o);
      setEntries(updated); onUpdate?.(updated);
      toast.success(`Status: ${newState}`);
    } catch { toast.error('Failed to update status'); }
  };

  const handleAttach = async (files) => {
    if (!attachTarget) return;
    try {
      for (const file of Array.from(files)) {
        const dataUrl = await readFileAsDataUrl(file);
        const att = { id: crypto.randomUUID?.() || Math.random().toString(36).slice(2), name: file.name, size: file.size, data_url: dataUrl };
        const ord = entries.find(e => e.id === attachTarget);
        const next = [...(ord?.attachments || []), att];
        await api.put(`/projects/${projectId}/orders/${attachTarget}`, { attachments: next });
        const updated = entries.map(e => e.id === attachTarget ? { ...e, attachments: next } : e);
        setEntries(updated); onUpdate?.(updated);
      }
      toast.success('File attached');
    } catch { toast.error('Attach failed'); }
    setAttachTarget(null);
  };

  const removeAttachment = async (orderId, attId) => {
    const ord = entries.find(e => e.id === orderId);
    const next = (ord?.attachments || []).filter(a => a.id !== attId);
    try {
      await api.put(`/projects/${projectId}/orders/${orderId}`, { attachments: next });
      const updated = entries.map(e => e.id === orderId ? { ...e, attachments: next } : e);
      setEntries(updated); onUpdate?.(updated);
    } catch { toast.error('Failed to remove'); }
  };

  // Linked bill creation
  const openCreateBill = (mode) => {
    setBillForm({
      bill_type: 'Vendor Payment',
      vendor_name: form.vendor_name,
      bill_number: form.order_number,
      bill_date: form.order_date || today(),
      bill_amount: form.order_amount || '',
      notes: `Linked to order: ${form.item_description || form.order_number}`
    });
    setCreateBillFor(mode);
  };

  const saveLinkedBill = async () => {
    if (!billForm.vendor_name.trim() || !billForm.bill_amount) { toast.error('Vendor and amount required'); return; }
    try {
      const res = await api.post(`/projects/${projectId}/billing`, { ...billForm, bill_amount: Number(billForm.bill_amount) });
      const newBill = res.data;
      const newBills = [...bills, newBill];
      setBills(newBills); onBillingUpdate?.(newBills);
      setForm(prev => ({ ...prev, bill_id: newBill.id }));
      toast.success('Bill created and linked');
      setCreateBillFor(null);
    } catch { toast.error('Failed to create bill'); }
  };

  const exportExcel = () => {
    import('xlsx').then(XLSX => {
      import('file-saver').then(({ saveAs }) => {
        const data = enriched.map(o => ({
          'Order No.': o.order_number, 'Vendor': o.vendor_name, 'Item': o.item_description,
          'Order Amount': o.order_amount, 'Order Date': o.order_date,
          'Tentative Delivery': o.tentative_delivery_date, 'Actual Delivery': o.actual_delivery_date,
          'Status': o.status, 'Computed': o._state.label, 'Delay (days)': o._delay ?? '',
          'Bill Linked': o._linkedBill ? o._linkedBill.bill_number || o._linkedBill.id : '',
          'Bill Amount': o._linkedBill?.bill_amount ?? '', 'Bill Paid': o._billPaid, 'Bill Pending': o._billPending,
          'Notes': o.notes,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Orders');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf]), `Orders-${projectName || 'Project'}.xlsx`);
        toast.success('Exported');
      });
    });
  };

  const FormBody = ({ mode }) => {
    const linkedBill = form.bill_id ? bills.find(b => b.id === form.bill_id) : null;
    const amountMismatch = linkedBill && Number(form.order_amount || 0) > 0 && Math.abs(Number(form.order_amount) - Number(linkedBill.bill_amount || 0)) > 1;
    return (
      <div className="space-y-3 mt-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Order Number</Label>
            <Input value={form.order_number} onChange={e => setForm({ ...form, order_number: e.target.value })}
              className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="PO-001"
              data-testid={`order-form-${mode}-number`} />
          </div>
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Vendor / Party *</Label>
            <div className="mt-1">
              <VendorSelect value={form.vendor_name} onChange={(name) => setForm({ ...form, vendor_name: name })}
                testId={`order-form-${mode}-vendor`} />
            </div>
            {mode === 'add' && recommendedVendor && !form.vendor_name && (
              <button type="button" onClick={() => setForm({ ...form, vendor_name: recommendedVendor })}
                className="text-[10px] text-[#3A8C8C] mt-1 hover:underline" data-testid={`order-form-${mode}-suggest-vendor`}>
                Use recent: {recommendedVendor}
              </button>
            )}
          </div>
        </div>
        <div>
          <Label className="text-xs text-[#6B7280] uppercase">Item Description *</Label>
          <Input value={form.item_description} onChange={e => setForm({ ...form, item_description: e.target.value })}
            className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="e.g. Italian marble tiles 200 sqft"
            data-testid={`order-form-${mode}-item`} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Order Date</Label>
            <Input type="date" value={form.order_date} onChange={e => setForm({ ...form, order_date: e.target.value })}
              className="mt-1 h-10 rounded-xl border-[#D4D0C8]" />
          </div>
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Tentative Delivery</Label>
            <Input type="date" value={form.tentative_delivery_date} onChange={e => setForm({ ...form, tentative_delivery_date: e.target.value })}
              className="mt-1 h-10 rounded-xl border-[#D4D0C8]" />
          </div>
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Order Amount (Rs)</Label>
            <Input type="number" value={form.order_amount} onChange={e => setForm({ ...form, order_amount: e.target.value })}
              className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="0"
              data-testid={`order-form-${mode}-amount`} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Delivery Location / Site</Label>
            <Input value={form.delivery_location} onChange={e => setForm({ ...form, delivery_location: e.target.value })}
              className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="Site address" />
          </div>
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger>
              <SelectContent>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {form.status === 'Delivered' && (
          <div className="grid grid-cols-3 gap-3 px-3 py-3 rounded-xl bg-[#7DA68A]/5 border border-[#7DA68A]/20">
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Actual Delivery Date</Label>
              <Input type="date" value={form.actual_delivery_date} onChange={e => setForm({ ...form, actual_delivery_date: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Time</Label>
              <Input type="time" value={form.actual_delivery_time} onChange={e => setForm({ ...form, actual_delivery_time: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Received By</Label>
              <Input value={form.received_by} onChange={e => setForm({ ...form, received_by: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="Name" />
            </div>
          </div>
        )}

        {/* Bill linkage */}
        <div className="space-y-2">
          <Label className="text-xs text-[#6B7280] uppercase">Linked Bill</Label>
          <div className="flex items-center gap-2">
            <Select value={form.bill_id || 'none'} onValueChange={v => setForm({ ...form, bill_id: v === 'none' ? null : v })}>
              <SelectTrigger className="h-10 rounded-xl border-[#D4D0C8] flex-1" data-testid={`order-form-${mode}-bill`}>
                <SelectValue placeholder="No bill linked" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {bills.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.bill_number || 'No #'} · {b.vendor_name} · Rs {formatINR(b.bill_amount)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button type="button" onClick={() => openCreateBill(mode)}
              className="h-10 px-3 rounded-xl border border-[#3A8C8C] text-xs text-[#3A8C8C] hover:bg-[#3A8C8C]/5 transition-colors flex items-center gap-1"
              data-testid={`order-form-${mode}-create-bill`}>
              <Plus size={12} /> Create linked bill
            </button>
          </div>
          {linkedBill && (
            <div className="px-3 py-2 rounded-lg bg-[#FAFAF8] border border-[#E8E6E1] text-[10px] text-[#6B7280] flex items-center justify-between">
              <span>Bill: Rs {formatINR(linkedBill.bill_amount)} · Paid Rs {formatINR((linkedBill.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0))}</span>
              {amountMismatch && (
                <span className="flex items-center gap-1 text-[#C4727F] font-semibold">
                  <AlertTriangle size={10} /> Amount mismatch
                </span>
              )}
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            className="mt-1 w-full h-16 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm resize-none" />
        </div>

        {mode === 'add' ? (
          <button onClick={() => submitForm('add')}
            className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors"
            data-testid="order-form-add-submit">
            Add Order
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setShowEdit(null)} className="flex-1 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">Cancel</button>
            <button onClick={() => submitForm('edit')}
              className="flex-1 h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors"
              data-testid="order-form-edit-submit">
              Save Changes
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="project-orders">
      {/* Computation strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="orders-computation-strip">
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Orders</p>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1">{totals.count}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Value</p>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1">Rs {formatINR(totals.totalValue)}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Delayed</p>
          <p className="text-lg font-bold text-[#C4727F] font-['Manrope'] mt-1">{totals.delayed}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Delivered</p>
          <p className="text-lg font-bold text-[#7DA68A] font-['Manrope'] mt-1">{totals.delivered}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Delivered · Unpaid</p>
          <p className="text-lg font-bold text-[#C67D5B] font-['Manrope'] mt-1">{totals.deliveredUnpaid}</p>
        </div>
      </div>

      {/* Quick filters + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-[#6B7280]" />
          {[
            { key: 'all', label: 'All' },
            { key: 'delayed', label: 'Delayed' },
            { key: 'due_today', label: 'Due Today' },
            { key: 'delivered', label: 'Delivered' },
            { key: 'high_value', label: 'High Value' },
          ].map(f => (
            <button key={f.key} onClick={() => setQuickFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors ${quickFilter === f.key ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#6B7280] hover:bg-[#D4D0C8]'}`}
              data-testid={`orders-quick-filter-${f.key}`}>
              {f.label}
            </button>
          ))}
          <Select value={filterState} onValueChange={setFilterState}>
            <SelectTrigger className="h-8 w-36 rounded-lg border-[#E8E6E1] text-[11px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-32 rounded-lg border-[#E8E6E1] text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="urgency">By urgency</SelectItem>
              <SelectItem value="date">By date</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <button onClick={exportExcel} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors" data-testid="orders-export">
            <Download size={13} /> Export
          </button>
          <button onClick={() => { setForm({ ...emptyForm, vendor_name: recommendedVendor || '' }); setShowAdd(true); }}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
            data-testid="orders-add-entry">
            <Plus size={14} /> Add Order
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="space-y-2" data-testid="orders-grid">
        {filtered.map(o => {
          const isExpanded = expanded[o.id];
          const StateIcon = STATE_ICON[o.status] || Package;
          return (
            <div key={o.id} className="glass-card rounded-xl overflow-hidden" data-testid={`order-entry-${o.id}`}>
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF8]/50 transition-colors group"
                onClick={() => setExpanded(prev => ({ ...prev, [o.id]: !prev[o.id] }))}>
                <button className="shrink-0">
                  {isExpanded ? <ChevronDown size={14} className="text-[#6B7280]" /> : <ChevronRight size={14} className="text-[#6B7280]" />}
                </button>
                <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-3 min-w-0">
                    <p className="text-sm font-medium text-[#2C2C2E] truncate">{o.item_description || '—'}</p>
                    <p className="text-[10px] text-[#6B7280] truncate">{o.order_number ? `#${o.order_number} · ` : ''}{o.vendor_name}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-semibold text-[#2C2C2E]">Rs {formatINR(o.order_amount)}</p>
                    <p className="text-[9px] text-[#6B7280]">Order Amt</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <p className="text-xs text-[#6B7280]">{formatDate(o.tentative_delivery_date)}</p>
                    <p className="text-[9px] text-[#6B7280]">Tentative</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <p className="text-xs text-[#6B7280]">{formatDate(o.actual_delivery_date)}</p>
                    <p className="text-[9px] text-[#6B7280]">Actual</p>
                  </div>
                  <div className="col-span-2 flex flex-col items-start gap-1">
                    <Badge className={`text-[8px] flex items-center gap-1 ${STATE_COLOR[o.status] || ''}`}>
                      <StateIcon size={9} /> {o.status}
                    </Badge>
                    <Badge className={`text-[8px] ${o._state.color}`}>{o._state.label}{o._delay > 0 ? ` · ${o._delay}d` : ''}</Badge>
                  </div>
                  <div className="col-span-1 flex flex-col items-end gap-1">
                    {o._linkedBill ? (
                      <Badge className="text-[8px] bg-[#3A8C8C]/10 text-[#3A8C8C] flex items-center gap-1">
                        <Link2 size={8} /> {o._billPending > 0 ? `Rs ${formatINR(o._billPending)}` : 'Paid'}
                      </Badge>
                    ) : (
                      <span className="text-[9px] text-[#D4D0C8]">No bill</span>
                    )}
                    {(o.attachments || []).length > 0 && <Paperclip size={10} className="text-[#6B7280]" />}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button onClick={e => { e.stopPropagation(); openEdit(o); }}
                    className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10" title="Edit" data-testid={`order-edit-${o.id}`}>
                    <Pencil size={13} className="text-[#3A8C8C]" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); setAttachTarget(o.id); fileRef.current?.click(); }}
                    className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10" title="Attach" data-testid={`order-attach-${o.id}`}>
                    <Paperclip size={13} className="text-[#6B7280]" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); setDeleteTarget(o.id); }}
                    className="p-1.5 rounded-lg hover:bg-[#C4727F]/10" title="Delete" data-testid={`order-delete-${o.id}`}>
                    <Trash2 size={13} className="text-[#C4727F]" />
                  </button>
                </div>
              </div>

              {/* Expansion */}
              {isExpanded && (
                <div className="border-t border-[#E8E6E1] bg-[#FAFAF8]/30 px-4 py-4 space-y-4" data-testid={`order-expanded-${o.id}`}>
                  {/* Detail grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div><span className="text-[#6B7280]">Order Date:</span> <span className="font-medium text-[#2C2C2E] ml-1">{formatDate(o.order_date)}</span></div>
                    <div><span className="text-[#6B7280]">Order Age:</span> <span className="font-medium text-[#2C2C2E] ml-1">{o._orderAge}d</span></div>
                    <div><span className="text-[#6B7280]">Location:</span> <span className="font-medium text-[#2C2C2E] ml-1">{o.delivery_location || '-'}</span></div>
                    <div><span className="text-[#6B7280]">Received By:</span> <span className="font-medium text-[#2C2C2E] ml-1">{o.received_by || '-'}</span></div>
                  </div>
                  {o.notes && <p className="text-xs text-[#6B7280]">{o.notes}</p>}

                  {/* State machine */}
                  <div>
                    <h5 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Delivery Timeline</h5>
                    <div className="flex items-center gap-1">
                      {STATES.map((s, i) => {
                        const sIdx = STATES.indexOf(o.status);
                        const isCompleted = i <= sIdx;
                        const isCurrent = i === sIdx;
                        return (
                          <button key={s} onClick={() => transition(o.id, s)} type="button"
                            className={`flex-1 px-2 py-1.5 rounded-lg text-[9px] font-medium border transition-colors ${isCurrent ? 'border-[#3A8C8C] bg-[#3A8C8C] text-white' : isCompleted ? 'border-[#7DA68A]/30 bg-[#7DA68A]/10 text-[#7DA68A]' : 'border-[#E8E6E1] bg-white text-[#6B7280] hover:bg-[#FAFAF8]'}`}
                            data-testid={`order-transition-${o.id}-${s.replace(/\s+/g, '-')}`}>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Linked billing */}
                  <div>
                    <h5 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Linked Billing</h5>
                    {o._linkedBill ? (
                      <div className="px-3 py-2.5 rounded-lg bg-white border border-[#E8E6E1] flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-[#2C2C2E]">{o._linkedBill.bill_number || 'No bill #'} · {o._linkedBill.vendor_name}</p>
                          <p className="text-[10px] text-[#6B7280]">Bill Rs {formatINR(o._linkedBill.bill_amount)} · Paid Rs {formatINR(o._billPaid)} · Pending Rs {formatINR(o._billPending)}</p>
                        </div>
                        {o._amountMismatch && (
                          <Badge className="bg-[#C4727F]/10 text-[#C4727F] text-[8px] flex items-center gap-1">
                            <AlertTriangle size={9} /> Amount mismatch
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <p className="text-[10px] text-[#6B7280]">No bill linked. Use the Edit dialog to link or create one.</p>
                    )}
                  </div>

                  {/* Vendor history (rule-based) */}
                  <div>
                    <h5 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Vendor History (this project)</h5>
                    {(() => {
                      const vendorOrders = enriched.filter(e => e.vendor_name === o.vendor_name && e.id !== o.id);
                      if (!vendorOrders.length) return <p className="text-[10px] text-[#6B7280]">No prior orders with this vendor on this project.</p>;
                      const delivered = vendorOrders.filter(e => e.status === 'Delivered');
                      const avgDelay = delivered.length ? Math.round(delivered.reduce((s, e) => s + (e._delay || 0), 0) / delivered.length) : null;
                      return (
                        <div className="px-3 py-2 rounded-lg bg-white border border-[#E8E6E1] grid grid-cols-3 gap-3 text-[10px] text-[#6B7280]">
                          <div>Total orders: <span className="font-medium text-[#2C2C2E]">{vendorOrders.length}</span></div>
                          <div>Delivered: <span className="font-medium text-[#2C2C2E]">{delivered.length}</span></div>
                          <div>Avg delay: <span className="font-medium text-[#2C2C2E]">{avgDelay == null ? '—' : `${avgDelay}d`}</span></div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Attachments */}
                  {(o.attachments || []).length > 0 && (
                    <div>
                      <h5 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Attachments</h5>
                      <div className="flex flex-wrap gap-2">
                        {(o.attachments || []).map(a => (
                          <div key={a.id} className="group/att flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-[#E8E6E1] hover:border-[#3A8C8C] transition-colors">
                            <FileText size={11} className="text-[#3A8C8C]" />
                            <button type="button" onClick={() => setPreviewAtt(a)}
                              className="text-[10px] text-[#2C2C2E] hover:text-[#3A8C8C]"
                              data-testid={`order-attachment-${a.id}`}
                              title="Click to preview">
                              {a.name}
                            </button>
                            <button onClick={() => removeAttachment(o.id, a.id)} className="opacity-0 group-hover/att:opacity-100 ml-1">
                              <X size={9} className="text-[#C4727F]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Truck size={32} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">{entries.length === 0 ? 'No orders yet' : 'No orders match these filters'}</p>
          </div>
        )}
      </div>

      <input ref={fileRef} type="file" className="hidden" onChange={e => { handleAttach(e.target.files); e.target.value = ''; }} />

      {/* Add Order Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" data-testid="order-add-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Add Order</DialogTitle></DialogHeader>
          <FormBody mode="add" />
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={!!showEdit} onOpenChange={(o) => { if (!o) { setShowEdit(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-lg" data-testid="order-edit-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Edit Order</DialogTitle></DialogHeader>
          <FormBody mode="edit" />
        </DialogContent>
      </Dialog>

      {/* Inline Create Linked Bill */}
      <Dialog open={!!createBillFor} onOpenChange={(o) => { if (!o) setCreateBillFor(null); }}>
        <DialogContent className="max-w-md" data-testid="order-create-bill-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Create Linked Bill</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Vendor / Party *</Label>
                <div className="mt-1">
                  <VendorSelect value={billForm.vendor_name} onChange={(name) => setBillForm({ ...billForm, vendor_name: name })} testId="order-bill-vendor" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Bill Number</Label>
                <Input value={billForm.bill_number} onChange={e => setBillForm({ ...billForm, bill_number: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Bill Date</Label>
                <Input type="date" value={billForm.bill_date} onChange={e => setBillForm({ ...billForm, bill_date: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Bill Amount (Rs) *</Label>
                <Input type="number" value={billForm.bill_amount} onChange={e => setBillForm({ ...billForm, bill_amount: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="order-bill-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <Input value={billForm.notes} onChange={e => setBillForm({ ...billForm, notes: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setCreateBillFor(null)} className="flex-1 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
              <button onClick={saveLinkedBill}
                className="flex-1 h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A]"
                data-testid="order-bill-save">
                Create &amp; Link
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Delete Order?</DialogTitle></DialogHeader>
          <p className="text-xs text-[#6B7280] mt-1">This order will be permanently deleted. The linked bill (if any) will not be removed.</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setDeleteTarget(null)} className="flex-1 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
            <button onClick={deleteEntry} className="flex-1 h-9 rounded-lg bg-[#C4727F] text-white text-xs font-medium hover:bg-[#B5616E]" data-testid="order-delete-confirm">Delete</button>
          </div>
        </DialogContent>
      </Dialog>

      <AttachmentPreview attachment={previewAtt} onClose={() => setPreviewAtt(null)} />
    </div>
  );
}
